"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const DEFAULT_IMAGE_BASE = "/image/new-version/molding/plastic-injection-molding/quality-control";

const BLOCK_KEYS = [
	{ titleKey: "block1Title", descKey: "block1Desc" },
	{ titleKey: "block2Title", descKey: "block2Desc" },
	{ titleKey: "block3Title", descKey: "block3Desc" },
] as const;

/** stacked：居中标题+引言后双栏；split：左图右文顶对齐，标题与引言在右侧列（激光 QA 设计稿） */
export type PlasticInjectionMoldingQualityControlLayout = "stacked" | "split";

export interface PlasticInjectionMoldingQualityControlProps {
	/** 「Learn More」外链，默认上传页 */
	learnMoreHref?: string;
	/** 翻译命名空间，默认 plasticInjectionMoldingQualityControl；激光页可传 laserCuttingQualityAssurance */
	translationNamespace?: string;
	/** 四宫格图片目录（不含末尾 /），默认注塑 QC 目录；可传「Laser Cutting/...」等含空格路径（内部会 encodeURI） */
	imageBase?: string;
	/** 默认 stacked；split 时淡奶黄底、左侧四宫格白色细缝、右侧标题+引言+三节+按钮顶对齐 */
	layout?: PlasticInjectionMoldingQualityControlLayout;
}

function ImageGrid({
	imageBase,
	translationNamespace,
	gapClass,
}: {
	imageBase: string;
	translationNamespace: string;
	gapClass: string;
}) {
	const t = useTranslations(translationNamespace);
	const buildImageSrc = (n: number) => {
		// Support both encoded (%20) and unencoded (space) incoming bases without double-encoding.
		const normalizedBase = decodeURI(imageBase);
		return encodeURI(`${normalizedBase}/${n}.webp`);
	};
	return (
		<div className={`grid w-full grid-cols-2 ${gapClass}`}>
			{[1, 2, 3, 4].map((n) => (
				<div key={n} className="relative aspect-[4/4.4] w-full overflow-hidden bg-gray-200">
					<Image
						src={buildImageSrc(n)}
						alt={t("imageAlt", { n })}
						fill
						className="object-cover"
						sizes="(max-width: 1024px) 50vw, 25vw"
					/>
				</div>
			))}
		</div>
	);
}

function BlockList({
	translationNamespace,
	learnMoreHref,
	subheadingClass,
}: {
	translationNamespace: string;
	learnMoreHref: string;
	subheadingClass: string;
}) {
	const t = useTranslations(translationNamespace);
	return (
		<>
			{BLOCK_KEYS.map((keys, index) => (
				<div key={keys.titleKey} className={index < BLOCK_KEYS.length - 1 ? "mb-7 sm:mb-8" : "mb-7 sm:mb-8"}>
					<h3 className={`mb-2 text-base font-semibold sm:text-lg ${subheadingClass}`}>{t(keys.titleKey)}</h3>
					<p className="text-sm leading-relaxed text-black md:text-base">{t(keys.descKey)}</p>
				</div>
			))}
			<a
				href={learnMoreHref}
				target="_blank"
				rel="noopener noreferrer"
				className="inline-flex w-fit items-center justify-center rounded-lg bg-[#76a734] px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 sm:px-8 sm:py-3.5 sm:text-base"
			>
				{t("learnMoreButton")}
			</a>
		</>
	);
}

/**
 * 注塑质量控制：顶区标题+引言；左 2×2 图；右三段说明 + 绿按钮。
 * layout=split：与设计稿一致，左侧四宫格白缝，右侧大标题+引言+三节顶对齐。
 */
export default function PlasticInjectionMoldingQualityControl({
	learnMoreHref = "/upload",
	translationNamespace = "plasticInjectionMoldingQualityControl",
	imageBase = DEFAULT_IMAGE_BASE,
	layout = "stacked",
}: PlasticInjectionMoldingQualityControlProps) {
	const t = useTranslations(translationNamespace);

	if (layout === "split") {
		return (
			<section className="w-full bg-[#FCFFF4] py-8 sm:py-10 md:py-12 lg:py-16">
				<div className="container">
					<div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-12 xl:gap-16">
						{/* 左：四宫格，白底细缝（缝为白色） */}
						<div className="w-full bg-white p-1 sm:p-1.5">
							<ImageGrid
								imageBase={imageBase}
								translationNamespace={translationNamespace}
								gapClass="gap-1.5 bg-white sm:gap-2"
							/>
						</div>

						{/* 右：标题与引言置顶，与左侧图网格顶对齐；全文左对齐 */}
						<div className="flex min-w-0 flex-col text-left">
							<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
								{t("title")}
							</h2>
							<p className="mb-8 text-sm leading-relaxed text-black sm:mb-10 md:text-base">{t("intro")}</p>
							<BlockList
								translationNamespace={translationNamespace}
								learnMoreHref={learnMoreHref}
								subheadingClass="text-[#76a43f]"
							/>
						</div>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="w-full bg-[#FCFFF4] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 text-center text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("title")}
				</h2>
				<p className="mb-6 text-center text-sm leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{t("intro")}
				</p>

				<div className="grid grid-cols-1 items-start gap-10 text-left lg:grid-cols-2 lg:gap-12 xl:gap-16">
					<ImageGrid
						imageBase={imageBase}
						translationNamespace={translationNamespace}
						gapClass="gap-3 sm:gap-4"
					/>

					<div className="flex min-w-0 flex-col">
						<BlockList
							translationNamespace={translationNamespace}
							learnMoreHref={learnMoreHref}
							subheadingClass="text-[#76a734]"
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
